import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Box, CircularProgress
} from '@mui/material';
import axios from 'axios';

function LoginDialog({ open, onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    if (!username || !password) {
      setError('Username and password required');
      setLoading(false);
      return;
    }
    try {
      await axios.post('/dashboard-api/login', { username, password });
      onLogin();
    } catch (e) {
      setError('Invalid username or password');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Admin Login</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Username"
          fullWidth
          value={username}
          onChange={e => setUsername(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()}
        />
        <TextField
          margin="dense"
          label="Password"
          type="password"
          fullWidth
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogin} disabled={loading}>Login</Button>
      </DialogActions>
    </Dialog>
  );
}

function ServiceTable({ services, onAction, loading }) {
  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Service</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Uptime</TableCell>
            <TableCell>CPU</TableCell>
            <TableCell>Memory</TableCell>
            <TableCell>URL</TableCell>
            <TableCell>Controls</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map(s => (
            <TableRow key={s.name}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.status}</TableCell>
              <TableCell>{s.uptime ? `${Math.floor((Date.now() - s.uptime)/60000)} min` : '-'}</TableCell>
              <TableCell>{s.cpu != null ? s.cpu + '%' : '-'}</TableCell>
              <TableCell>{s.memory != null ? `${Math.round(s.memory/1024/1024)} MB` : '-'}</TableCell>
              <TableCell>
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener noreferrer">{s.url.replace('http://', '').replace('https://', '')}</a>
                ) : '-'}
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => onAction('start', s.name)} disabled={loading}>Start</Button>
                <Button size="small" onClick={() => onAction('stop', s.name)} disabled={loading}>Stop</Button>
                <Button size="small" onClick={() => onAction('restart', s.name)} disabled={loading}>Restart</Button>
                <Button size="small" onClick={() => onAction('logs', s.name)} disabled={loading}>Logs</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LogsDialog({ open, logs, onClose, service }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Logs for {service}</DialogTitle>
      <DialogContent>
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
          <b>stdout:</b>\n{logs?.out || ''}\n\n<b>stderr:</b>\n{logs?.err || ''}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

import GitPanel from './GitPanel';

export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsService, setLogsService] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const [tab, setTab] = useState('services');

  const fetchServices = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/dashboard-api/services');
      setServices(data);
    } catch {
      setSnackbar({ open: true, msg: 'Failed to fetch services', severity: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (loggedIn && tab === 'services') fetchServices();
  }, [loggedIn, tab]);

  const handleAction = async (action, name) => {
    setLoading(true);
    try {
      if (action === 'logs') {
        const { data } = await axios.get(`/dashboard-api/service/${name}/logs`);
        setLogs(data);
        setLogsService(name);
        setLogsOpen(true);
      } else {
        await axios.post(`/dashboard-api/service/${name}/${action}`);
        setSnackbar({ open: true, msg: `${action}ed ${name}`, severity: 'success' });
        fetchServices();
      }
    } catch {
      setSnackbar({ open: true, msg: `Failed to ${action} ${name}`, severity: 'error' });
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await axios.post('/dashboard-api/logout');
    setLoggedIn(false);
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Elzware-HCE Admin Dashboard</Typography>
          {loggedIn && <Button color="inherit" onClick={handleLogout}>Logout</Button>}
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 4 }}>
        {!loggedIn && <LoginDialog open={!loggedIn} onLogin={() => setLoggedIn(true)} />}
        {loggedIn && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button variant={tab === 'services' ? 'contained' : 'outlined'} sx={{ mr: 1 }} onClick={() => setTab('services')}>Service Overview</Button>
              <Button variant={tab === 'git' ? 'contained' : 'outlined'} onClick={() => setTab('git')}>Git Status & Rebuild</Button>
            </Box>
            {tab === 'services' && (
              <>
                <Typography variant="h5">Service Overview</Typography>
                {loading ? <CircularProgress sx={{ mt: 2 }} /> :
                  <ServiceTable services={services} onAction={handleAction} loading={loading} />}
                <LogsDialog open={logsOpen} logs={logs} service={logsService} onClose={() => setLogsOpen(false)} />
              </>
            )}
            {tab === 'git' && <GitPanel />}
          </>
        )}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
